import { defineStore } from 'pinia'
import { completeOnboarding as apiCompleteOnboarding, getProfile } from '@/api/endpoints/profile'
import type { User } from '@/api/types'
import AppConfig from '@/config'
import { isTcell } from '@/config/tenant'
import { computed, ref } from 'vue'

export const useProfileStore = defineStore('profile', () => {
	const msisdn = ref(0)
	const email = ref('')
	const plan = ref('')
	const onboardingComplete = ref<boolean | null>(null)
	// Профиль уже запрашивали в этой сессии приложения (см. ensureProfile).
	const loaded = ref(false)

	const msisdnFormat = computed(() => {
		if (isTcell) {
			return msisdn.value
				? msisdn.value.toString().replace(/(992)(\d{2})(\d{3})(\d{2})(\d{2})/, '+$1 ($2) $3-$4-$5')
				: ''
		}
		const PHONE_FORMATS = {
			RU: {
				regex: /(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/,
				replace: '+$1 ($2) $3-$4-$5'
			},
			BY: {
				regex: /(375)(\d{2})(\d{3})(\d{2})(\d{2})/,
				replace: '+$1 ($2) $3$4$5'
			}
		}
		const local = AppConfig.local as keyof typeof PHONE_FORMATS
		return msisdn.value ? msisdn.value.toString().replace(PHONE_FORMATS[local].regex, PHONE_FORMATS[local].replace) : ''
	})

	async function fetchProfile() {
		const { data } = await getProfile()
		const user = data as User
		msisdn.value = user.msisdn ?? 0
		email.value = user.email ?? ''
		plan.value = user.plan ?? ''
		// null — данных ещё нет; false — онбординг не пройден (редирект на /onboarding после входа)
		onboardingComplete.value = typeof user.onboarding_complete === 'boolean' ? user.onboarding_complete : null
		loaded.value = true
	}

	/**
	 * Профиль нужен на главной ради plan (гейт pro-топиков), но грузить его на каждый
	 * переход незачем — тянем один раз за сессию приложения. Ошибку глушим: без plan
	 * гейт просто пропускает, экран из-за этого падать не должен.
	 */
	async function ensureProfile() {
		if (loaded.value) return
		try {
			await fetchProfile()
		} catch (error) {
			console.warn('профиль не загрузился', error)
		}
	}

	/**
	 * Онбординг пройден. Ошибку глушим намеренно: если флаг не переключился на сервере, экран
	 * покажется после следующего входа — но запирать пользователя на онбординге нельзя.
	 */
	async function completeOnboarding() {
		try {
			await apiCompleteOnboarding()
		} catch (error) {
			console.warn('onboarding-complete не прошёл', error)
		}
		onboardingComplete.value = true
	}

	return {
		msisdn,
		msisdnFormat,
		email,
		plan,
		onboardingComplete,
		fetchProfile,
		ensureProfile,
		completeOnboarding
	}
})
